import { AxiosProgressEvent, AxiosResponse, Method } from "axios";

import { api } from "../api";

/**
 * Request shapes for the two file-transfer patterns the migrated Retool
 * pages need (EN-5148). Page services call these instead of hand-rolling the
 * axios config, so every export and upload behaves the same way.
 */

export interface IFileDownloadRequest {
  url: string;
  /** Retool's export queries are mostly POSTs with a JSON body. */
  method?: Method;
  data?: unknown;
  params?: Record<string, string | number | undefined>;
}

export interface IFileUploadRequest {
  url: string;
  /** POST for creates (bulk import), PUT for replaces (tenant logo). */
  method?: Method;
  formData: FormData;
  onUploadProgress?: (percent: number) => void;
}

export const fileTransferService = {
  /**
   * Fetches an endpoint's file as a blob. The response is NOT checked here —
   * pass it to `downloadFromResponse` (or `useFileDownload`), which rejects
   * non-2xx before anything reaches the disk.
   */
  async download({
    url,
    method = "GET",
    data,
    params,
  }: IFileDownloadRequest): Promise<AxiosResponse<Blob>> {
    return api.request<Blob>({
      url,
      method,
      data,
      params,
      responseType: "blob",
    });
  },

  /**
   * Posts a multipart body.
   *
   * `Content-Type: undefined` is load-bearing, not tidiness. The shared `api`
   * instance defaults every request to `application/json`, and axios reacts
   * to a JSON content type on FormData by **replacing the body with
   * `JSON.stringify(formDataToJSON(data))`** — the request would leave with
   * no file at all and .NET's `[FromForm]` binding would see nothing.
   * Clearing the header puts the request back on axios's normal FormData
   * path, where the browser sets `multipart/form-data` with its boundary.
   */
  async upload<T = unknown>({
    url,
    method = "POST",
    formData,
    onUploadProgress,
  }: IFileUploadRequest): Promise<AxiosResponse<T>> {
    return api.request<T>({
      url,
      method,
      data: formData,
      headers: { "Content-Type": undefined },
      onUploadProgress: onUploadProgress
        ? (event: AxiosProgressEvent) => {
            const total = event.total ?? 0;
            onUploadProgress(
              total > 0 ? Math.round((event.loaded * 100) / total) : 0
            );
          }
        : undefined,
    });
  },
};
